import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchDisclosureNews,
  INSTRUCTIONS_API_FETCH_SIZE,
  INSTRUCTIONS_API_SCROLL_POS,
} from '@apis/instructions';
import type {
  DisclosureNewsApiLabel,
  DisclosureNewsResult,
} from '@app-types/instructionsApi';
import type { InstructionsInfoTabKey } from '@app-types/instructions';
import { getAccessToken } from '@utils/authStorage';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';
import { DISCLOSURE_API_LABEL_BY_TAB } from '@pages/Instructions/components/instructionsInfoFilters';

const YYYYMMDD = /^\d{8}$/;

export const buildDisclosureNewsQueryKey = (
  ticker: string | undefined,
  mkt: string | undefined,
  label: DisclosureNewsApiLabel,
  from: string | null,
  to: string | null,
) => [
  'disclosures',
  'news',
  ticker,
  mkt,
  label,
  from,
  to,
  INSTRUCTIONS_API_FETCH_SIZE,
  INSTRUCTIONS_API_SCROLL_POS,
] as const;

/** Fetches one ranged 100-row disclosure batch for the selected inner tab. */
export const useDisclosureNews = (
  tabKey: InstructionsInfoTabKey,
  from: string | null,
  to: string | null,
) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const hasToken = Boolean(getAccessToken());
  const label = DISCLOSURE_API_LABEL_BY_TAB[tabKey];
  const hasRange = Boolean(from && to && YYYYMMDD.test(from) && YYYYMMDD.test(to));

  return useQuery<DisclosureNewsResult>({
    queryKey: buildDisclosureNewsQueryKey(
      ticker,
      mkt,
      label,
      from,
      to,
    ),
    queryFn: () => fetchDisclosureNews({
      ticker: ticker as string,
      mkt: mkt as string,
      label,
      from: from as string,
      to: to as string,
      fetchSize: INSTRUCTIONS_API_FETCH_SIZE,
      scrollPos: INSTRUCTIONS_API_SCROLL_POS,
    }),
    enabled: Boolean(ticker && mkt && hasToken && label && hasRange),
    staleTime: 60_000,
    // Switching away and back mounts this cached category again. The latest
    // contract requires every tab selection to refresh from the API.
    refetchOnMount: 'always',
    retry: 1,
  });
};
